import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchBar from '../components/Searchbar';
import Explore from '../components/Explore';
import PropertyCard from '../../UserDashboard/components/PropertyCard';
import UpcomingProjectsSection from '../components/UpcomingProjectsSection';
import { propertiesAPI } from '../../services/api.service';
import '../../UserDashboard/styles/BuyerHome.css';
import '../../UserDashboard/styles/PropertyCard.css';

/* Auto-scroll: same as Buyer Dashboard — smooth, infinite loop, pause on hover/touch */
const MARQUEE_SPEED_PX_PER_SEC = 40;

const Home = () => {
  window.scrollTo(0,0);
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);
  const firstSetRef = useRef(null);
  const isHoverPausedRef = useRef(false);
  const rafIdRef = useRef(null);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const response = await propertiesAPI.list({ limit: 50 });
        
        if (response.success && response.data && response.data.properties) {
          // Convert backend properties to frontend format (matching Buyer Dashboard)
          const backendProperties = response.data.properties.map(prop => {
            // Get the best image (cover_image or first image from array)
            let imageUrl = null;
            
            if (prop.cover_image && prop.cover_image.trim() !== '') {
              imageUrl = prop.cover_image;
            } else if (Array.isArray(prop.images) && prop.images.length > 0) {
              const validImage = prop.images.find(img => img && img.trim() !== '');
              imageUrl = validImage || null;
            }
            
            // Fallback to placeholder if no valid image found
            if (!imageUrl || imageUrl.trim() === '') {
              imageUrl = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=500';
            }
            
            return {
              id: prop.id,
              image: imageUrl,
              title: prop.title,
              price: parseFloat(prop.price),
              location: prop.location,
              bedrooms: prop.bedrooms || '0',
              bathrooms: prop.bathrooms || '0',
              area: parseFloat(prop.area),
              type: prop.property_type,
              status: prop.status === 'sale' ? 'For Sale' : (prop.status === 'rent' ? 'For Rent' : prop.status),
              // Additional fields for compatibility
              propertyType: prop.property_type,
              projectType: prop.project_type || null,
              description: prop.description || '',
              amenities: Array.isArray(prop.amenities) ? prop.amenities : (prop.amenities ? [prop.amenities] : []),
              images: Array.isArray(prop.images) ? prop.images : (prop.images ? [prop.images] : []),
              latitude: prop.latitude,
              longitude: prop.longitude,
              createdAt: prop.created_at,
              seller_name: prop.seller_name,
              seller_phone: prop.seller_phone
            };
          });
          
          // Filter out upcoming projects - they should only appear in Upcoming Projects section
          const regularProperties = backendProperties.filter(prop => 
            !prop.projectType || prop.projectType !== 'upcoming'
          );
          
          setProperties(regularProperties);
        } else {
          setProperties([]);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  const pauseAutoScroll = () => { isHoverPausedRef.current = true; };
  const resumeAutoScroll = () => { isHoverPausedRef.current = false; };

  useEffect(() => {
    if (properties.length === 0) return;
    const container = scrollContainerRef.current;
    const firstSet = firstSetRef.current;
    if (!container || !firstSet) return;

    let lastTime = performance.now();

    const tick = (now) => {
      const elapsed = (now - lastTime) / 1000;
      lastTime = now;
      if (!isHoverPausedRef.current) {
        const setWidth = firstSet.offsetWidth;
        if (setWidth > 0) {
          container.scrollLeft += MARQUEE_SPEED_PX_PER_SEC * elapsed;
          if (container.scrollLeft >= setWidth) {
            container.scrollLeft -= setWidth;
          }
        }
      }
      rafIdRef.current = requestAnimationFrame(tick);
    };
    rafIdRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [properties.length]);

  const handleViewAllClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/searchresults');
  };

  return (
    <div>
      <SearchBar />
      
      {/* Mixed Properties Section - Matching Buyer Dashboard structure */}
      <div className="buyer-home-properties-section">
        <div className="buyer-section-header">
          <div className="buyer-section-header-content">
            <div>
              <h1>Explore Properties</h1>
              <p>Buy or Rent — All in One Place</p>
            </div>
            <button 
              className="buyer-view-all-btn"
              onClick={handleViewAllClick}
              aria-label="View all properties"
            >
              View All
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading properties...</p>
          </div>
        ) : properties.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>No properties available at the moment.</p>
          </div>
        ) : (
          <div
            ref={scrollContainerRef}
            className="buyer-horizontal-scroll-container buyer-marquee-container"
            onMouseEnter={pauseAutoScroll}
            onMouseLeave={resumeAutoScroll}
            onTouchStart={pauseAutoScroll}
            onTouchEnd={resumeAutoScroll}
            aria-label="Explore properties carousel"
          >
            <div className="buyer-marquee-track">
              <div ref={firstSetRef} className="buyer-property-cards-wrapper buyer-marquee-set">
                {properties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
              <div className="buyer-property-cards-wrapper buyer-marquee-set">
                {properties.map((property) => (
                  <PropertyCard key={`${property.id}-dup`} property={property} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upcoming Projects Section */}
      <UpcomingProjectsSection />

      <Explore />
    </div>
  );
};

export default Home;